/* Metal Mulisha dealer locator. Loaded only by its Shopify section. */
(function () {
  'use strict';
  if (window.MMDealerLocator) return;
  const instances = new Map();
  const U = window.MMDealerLocatorUtils;
  if (!U) return;
  let mapsPromise;
  let mapsFailed = false;

  function loadMaps(apiKey) {
    if (mapsFailed) return Promise.reject(new Error('Map access is unavailable.'));
    if (window.google && window.google.maps && window.google.maps.Map && window.google.maps.Geocoder) return Promise.resolve(window.google.maps);
    if (!apiKey) return Promise.reject(new Error('Map access is not configured.'));
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise((resolve, reject) => {
      let settled = false;
      const callbackName = '__mmDealerMapsReady';
      const script = document.createElement('script');
      const timer = window.setTimeout(() => finish(new Error('Map request timed out.')), 15000);
      const previousAuthFailure = window.gm_authFailure;
      function finish(error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        if (error) {
          mapsFailed = true;
          reject(error);
        } else resolve(window.google.maps);
      }
      window.gm_authFailure = function () {
        mapsFailed = true;
        finish(new Error('Map access was not authorized.'));
        window.dispatchEvent(new CustomEvent('mm:dealer-map-error'));
        if (typeof previousAuthFailure === 'function') previousAuthFailure();
      };
      window[callbackName] = () => finish(window.google && window.google.maps ? null : new Error('Map unavailable.'));
      const url = new URL('https://maps.googleapis.com/maps/api/js');
      url.searchParams.set('key', apiKey);
      url.searchParams.set('callback', callbackName);
      url.searchParams.set('loading', 'async');
      url.searchParams.set('v', 'weekly');
      script.src = url.href;
      script.async = true;
      script.dataset.mmDealerMaps = 'true';
      script.onerror = () => finish(new Error('Map network request failed.'));
      document.head.append(script);
    });
    return mapsPromise;
  }

  function element(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value != null) node.textContent = value;
    return node;
  }
  function link(label, href, external) {
    const anchor = element('a', 'mm-dealer-card__link', label);
    anchor.href = href;
    if (external) { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; }
    return anchor;
  }
  async function fetchJSON(url, signal) {
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error('Dealer data could not be loaded.');
    return response.json();
  }
  function recordArray(data) {
    if (Array.isArray(data)) return data;
    return data && (data.dealers || data.records || data.stores) || [];
  }
  function sameOriginURL(value, base) {
    const url = new URL(value, base || window.location.href);
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('Unexpected dealer data URL.');
    return url;
  }
  async function loadDealerRecords(root, signal) {
    const source = root.querySelector('[data-dealers-json]');
    const backupURL = root.dataset.dealersUrl;
    async function assetRecords() {
      if (!backupURL) throw new Error('No dealer records are available.');
      let url = new URL(backupURL, window.location.href);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid dealer data URL.');
      const seen = new Set();
      const records = [];
      while (url) {
        if (seen.has(url.href)) throw new Error('Repeated dealer data page.');
        seen.add(url.href);
        const page = await fetchJSON(url.href, signal);
        const rows = recordArray(page);
        if (!Array.isArray(rows)) throw new Error('Invalid dealer record format.');
        records.push(...rows);
        url = page && !Array.isArray(page) && page.next_page_url ? sameOriginURL(page.next_page_url, url.href) : null;
      }
      return records;
    }
    if (!source || source.dataset.source === 'asset') return U.normalizeRecords(await assetRecords());
    if (source.dataset.sourceUnavailable === 'true') throw new Error('The selected Shopify dealer source is unavailable.');
    try {
      let pageScript = source;
      let base = window.location.href;
      const visited = new Set();
      const records = [];
      while (pageScript) {
        const rows = recordArray(JSON.parse(pageScript.textContent || '[]'));
        if (!Array.isArray(rows)) throw new Error('Invalid dealer record format.');
        records.push(...rows);
        const next = pageScript.dataset.nextUrl;
        if (!next) break;
        const url = sameOriginURL(next, base);
        const sectionID = pageScript.dataset.sectionId || root.dataset.sectionId;
        if (sectionID) url.searchParams.set('section_id', sectionID);
        if (visited.has(url.href)) throw new Error('Repeated dealer data page.');
        visited.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('Dealer data page unavailable.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        pageScript = doc.querySelector('[data-dealers-json]');
        if (!pageScript) throw new Error('Dealer data page incomplete.');
        base = url.href;
      }
      return U.normalizeRecords(records);
    } catch (error) {
      // Once the merchant chooses metaobjects, an old export must never resurrect
      // dealers that have since been removed or made inactive in Shopify.
      if (signal.aborted || source.dataset.source === 'metaobjects' || !backupURL) throw error;
      return U.normalizeRecords(await assetRecords());
    }
  }

  class DealerLocator {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.records = [];
      this.results = [];
      this.markers = [];
      this.cards = new Map();
      this.origin = null;
      this.query = '';
      this.originLabel = '';
      this.sequence = 0;
      this.selected = null;
      this.map = null;
      this.mapRequested = false;
      this.ready = false;
      this.apiKey = root.dataset.apiKey || '';
      const find = (selector) => root.querySelector(selector);
      this.form = find('[data-dealer-search-form]');
      this.input = find('[data-dealer-query]');
      this.radius = find('[data-dealer-radius]');
      this.list = find('[data-dealer-results]');
      this.status = find('[data-dealer-status]');
      this.count = find('[data-dealer-count]');
      this.empty = find('[data-dealer-empty]');
      this.canvas = find('[data-dealer-map]');
      this.mapStatus = find('[data-dealer-map-status]');
      this.placeholder = find('[data-dealer-map-placeholder]');
      this.locationButton = find('[data-dealer-location]');
      this.listen(this.form, 'submit', (event) => { event.preventDefault(); this.search(); });
      this.listen(this.radius, 'change', () => this.origin ? this.render() : this.search());
      this.listen(find('[data-dealer-clear]'), 'click', () => this.clear());
      this.listen(find('[data-dealer-expand]'), 'click', () => { if (this.origin && this.radius) { this.radius.value = 'all'; this.render(); } else this.clear(); });
      this.listen(this.locationButton, 'click', () => this.locate());
      this.listen(find('[data-dealer-load-map]'), 'click', () => this.chooseView('map'));
      root.querySelectorAll('[data-dealer-view]').forEach((button) => this.listen(button, 'click', () => this.chooseView(button.dataset.dealerView)));
      this.listen(window, 'mm:dealer-map-error', () => { if (this.mapRequested) this.mapError(); });
      this.setView(root.dataset.view || 'list');
      this.load();
    }
    listen(target, event, handler) { if (target) target.addEventListener(event, handler, { signal: this.abort.signal }); }
    say(message) { if (this.status) this.status.textContent = message; }
    async load() {
      this.root.setAttribute('aria-busy', 'true');
      this.say('Loading dealers…');
      try {
        this.records = await loadDealerRecords(this.root, this.abort.signal);
        if (this.abort.signal.aborted) return;
        this.ready = true;
        this.render();
      } catch (error) {
        if (!this.abort.signal.aborted) this.say('The dealer list could not load. Please refresh the page and try again.');
      } finally { this.root.removeAttribute('aria-busy'); }
    }
    busy(active) {
      if (this.form) this.form.setAttribute('aria-busy', String(active));
      if (this.locationButton) this.locationButton.disabled = active;
    }
    async search() {
      if (!this.ready) return;
      const query = (this.input && this.input.value || '').trim();
      if (!query) { this.clear(); return; }
      const sequence = ++this.sequence;
      this.origin = null;
      this.originLabel = '';
      this.query = query;
      const nameMatches = this.records.filter((record) => U.matches(record, query, true));
      const cityQuery = this.records.some((record) => U.normalize(record.city) === U.normalize(query));
      const postalQuery = /^\d{5}(?:-\d{4})?$/.test(query) || /^[a-z]\d[a-z]\s?\d[a-z]\d$/i.test(query);
      if ((nameMatches.length && !cityQuery && !postalQuery) || !this.apiKey) {
        this.busy(false);
        this.render();
        return;
      }
      this.busy(true);
      this.say('Finding dealers near “' + query + '”…');
      try {
        const maps = await loadMaps(this.apiKey);
        if (sequence !== this.sequence || this.abort.signal.aborted) return;
        const request = { address: query };
        if (this.records.every((record) => /^(us|usa|united states|united states of america)$/.test(U.normalize(record.country)))) request.region = 'us';
        const response = await new maps.Geocoder().geocode(request);
        if (sequence !== this.sequence || this.abort.signal.aborted) return;
        const first = response && response.results && response.results[0];
        if (!first || !first.geometry || !first.geometry.location) throw new Error('Location not found.');
        const origin = U.coordinates({ lat: first.geometry.location.lat(), lng: first.geometry.location.lng() });
        if (!origin) throw new Error('Location not found.');
        this.origin = origin;
        this.originLabel = first.formatted_address || query;
        this.query = '';
        this.render();
      } catch (error) {
        if (sequence !== this.sequence || this.abort.signal.aborted) return;
        this.render();
        this.say(this.results.length ? 'Showing matching dealers. Nearby-distance search is unavailable right now.' : 'We could not locate that search. Try a city, ZIP/postal code, or dealer name, or clear your search to browse all dealers.');
      } finally { if (sequence === this.sequence) this.busy(false); }
    }
    locate() {
      if (!this.ready) return;
      if (!navigator.geolocation) { this.say('Your browser cannot share a location. Search by city or ZIP/postal code instead.'); return; }
      const sequence = ++this.sequence;
      this.busy(true);
      this.say('Waiting for your location permission…');
      navigator.geolocation.getCurrentPosition((position) => {
        if (sequence !== this.sequence || this.abort.signal.aborted) return;
        this.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.originLabel = 'your location';
        this.query = '';
        if (this.input) this.input.value = '';
        this.busy(false);
        this.render();
      }, (error) => {
        if (sequence !== this.sequence || this.abort.signal.aborted) return;
        this.busy(false);
        this.say(error.code === 1 ? 'Location access was declined. Search by city or ZIP/postal code instead.' : 'Your location is unavailable. Search by city or ZIP/postal code instead.');
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }
    clear() {
      ++this.sequence;
      this.origin = null;
      this.originLabel = '';
      this.query = '';
      if (this.input) this.input.value = '';
      this.busy(false);
      if (this.ready) this.render();
    }
    render() {
      if (!this.list) return;
      this.results = U.selectRecords(this.records, { query: this.query, origin: this.origin, radius: this.radius ? this.radius.value : 'all' });
      const fragment = document.createDocumentFragment();
      this.cards.clear();
      for (const record of this.results) fragment.append(this.card(record));
      this.list.replaceChildren(fragment);
      const count = this.results.length;
      if (this.count) this.count.textContent = count + (count === 1 ? ' dealer' : ' dealers');
      if (this.empty) this.empty.hidden = count > 0;
      if (this.origin) {
        const radius = this.radius && this.radius.value;
        const area = radius && radius !== 'all' ? ' within ' + radius + ' miles of ' : ' nearest to ';
        this.say(count ? count + (count === 1 ? ' dealer' : ' dealers') + area + this.originLabel + '. Distances are approximate.' : 'No dealers found' + area + this.originLabel + '. Try a wider distance or shop online.');
      } else if (this.query) this.say(count ? 'Showing dealers matching “' + this.query + '”.' : 'No dealers match “' + this.query + '”. Try another city or dealer name, or browse all dealers.');
      else this.say(count ? 'Search by city, ZIP/postal code, or dealer name. Selection varies by store; call ahead.' : 'No dealers are listed right now. You can shop the full collection online.');
      const expand = this.root.querySelector('[data-dealer-expand]');
      if (expand) expand.textContent = this.origin && this.radius && this.radius.value !== 'all' ? 'Search all distances' : 'View all dealers';
      if (this.map) this.updateMap();
    }
    card(record) {
      const card = element('article', 'mm-dealer-card');
      card.dataset.dealerId = record.id;
      const top = element('div', 'mm-dealer-card__top');
      top.append(element('h3', 'mm-dealer-card__name', record.name));
      if (record.distance !== null) top.append(element('span', 'mm-dealer-card__distance', (record.distance < 1 ? '<1' : Math.round(record.distance).toLocaleString()) + ' mi'));
      card.append(top);
      card.append(element('p', 'mm-dealer-card__address', U.fullAddress(record)));
      const actions = element('div', 'mm-dealer-card__actions');
      const directions = U.directionsURL(record);
      const phone = U.phoneURL(record.phone);
      const website = U.websiteURL(record.website);
      if (directions) { const route = link('Directions', directions, true); route.setAttribute('aria-label', 'Directions to ' + record.name + ' (opens Google Maps)'); actions.append(route); }
      if (phone) { const call = link('Call', phone, false); call.setAttribute('aria-label', 'Call ' + record.name + ': ' + record.phone); actions.append(call); }
      if (website) { const site = link('Website', website, true); site.setAttribute('aria-label', 'Visit ' + record.name + ' website (opens a new tab)'); actions.append(site); }
      if (this.apiKey && U.coordinates(record)) {
        const button = element('button', 'mm-dealer-card__map-button', 'Show on map');
        button.type = 'button';
        button.setAttribute('aria-label', 'Show ' + record.name + ' on map');
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.selectDealer(record.id, true));
        actions.append(button);
      }
      card.append(actions);
      this.cards.set(record.id, card);
      return card;
    }
    setView(view) {
      this.root.dataset.view = view === 'map' ? 'map' : 'list';
      this.root.querySelectorAll('[data-dealer-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.dealerView === this.root.dataset.view)));
    }
    async chooseView(view) {
      if (view !== 'map') { this.setView('list'); return; }
      this.setView('map');
      await this.ensureMap();
      if (this.map && !mapsFailed && this.root.dataset.view === 'map') {
        window.google.maps.event.trigger(this.map, 'resize');
        this.fitMapToResults();
      }
    }
    mapError() {
      const message = 'The map is unavailable right now. You can still search the dealer list and open Directions.';
      if (this.mapStatus) this.mapStatus.textContent = message;
      this.say(message);
      this.setView('list');
      if (this.placeholder) this.placeholder.hidden = false;
    }
    async ensureMap() {
      if (mapsFailed) { this.mapError(); return null; }
      if (this.map) return this.map;
      if (this.mapLoading) return this.mapLoading;
      if (!this.canvas || !this.apiKey) { this.mapError(); return null; }
      this.mapRequested = true;
      if (this.mapStatus) this.mapStatus.textContent = 'Loading map…';
      this.mapLoading = (async () => {
        try {
          const maps = await loadMaps(this.apiKey);
          if (this.abort.signal.aborted) return null;
          if (this.placeholder) this.placeholder.hidden = true;
          this.map = new maps.Map(this.canvas, {
            center: { lat: 39.5, lng: -98.35 }, zoom: 4, mapTypeControl: false,
            streetViewControl: false, fullscreenControl: true, gestureHandling: 'cooperative',
            clickableIcons: false
          });
          this.infoWindow = new maps.InfoWindow();
          this.infoWindow.addListener('closeclick', () => {
            if (!this.returnFocus || this.abort.signal.aborted) return;
            const viewToggle = this.root.querySelector('[data-dealer-view="' + this.root.dataset.view + '"]');
            const target = [this.returnFocus, viewToggle, this.input].find((node) => node && node.isConnected && node.getClientRects().length);
            this.returnFocus = null;
            if (target) target.focus();
          });
          if (this.mapStatus) this.mapStatus.textContent = 'Select a dealer marker for details and directions.';
          this.updateMap();
          return this.map;
        } catch (error) {
          if (!this.abort.signal.aborted) this.mapError();
          return null;
        } finally { this.mapLoading = null; }
      })();
      return this.mapLoading;
    }
    updateMap() {
      if (!this.map || !window.google || mapsFailed) return;
      const maps = window.google.maps;
      this.markers.forEach(({ marker }) => { maps.event.clearInstanceListeners(marker); marker.setMap(null); });
      this.markers = [];
      if (this.infoWindow) this.infoWindow.close();
      this.returnFocus = null;
      for (const record of this.results) {
        const position = U.coordinates(record);
        if (!position) continue;
        const marker = new maps.Marker({ position, map: this.map, title: record.name, optimized: false });
        marker.addListener('click', () => this.selectDealer(record.id, false));
        this.markers.push({ id: record.id, marker });
      }
      this.fitMapToResults();
      if (!this.markers.length && this.mapStatus) this.mapStatus.textContent = 'No map locations match this search. Try a wider distance or browse the dealer list.';
      else if (this.mapStatus) this.mapStatus.textContent = 'Select a dealer marker for details and directions.';
    }
    fitMapToResults() {
      // A hidden mobile map has no dimensions, so fit it again after revealing it.
      // Keep its existing markers and open dealer details intact during view changes.
      if (!this.map || !window.google || mapsFailed) return;
      if (this.markers.length === 1) { this.map.setCenter(this.markers[0].marker.getPosition()); this.map.setZoom(13); }
      else if (this.markers.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        this.markers.forEach(({ marker }) => bounds.extend(marker.getPosition()));
        this.map.fitBounds(bounds, 40);
      } else if (this.origin) { this.map.setCenter(this.origin); this.map.setZoom(9); }
    }
    async selectDealer(id, openMap) {
      const record = this.results.find((item) => item.id === id);
      if (!record) return;
      this.selected = id;
      this.cards.forEach((card, dealerID) => {
        card.classList.toggle('is-selected', dealerID === id);
        const button = card.querySelector('.mm-dealer-card__map-button');
        if (button) button.setAttribute('aria-pressed', String(dealerID === id));
      });
      if (openMap) this.setView('map');
      const map = await this.ensureMap();
      if (!map || this.selected !== id || this.abort.signal.aborted) return;
      if (openMap && this.root.dataset.view === 'map') {
        window.google.maps.event.trigger(map, 'resize');
        this.fitMapToResults();
      }
      const entry = this.markers.find((item) => item.id === id);
      if (!entry) return;
      const content = element('div', 'mm-dealer-map-info');
      content.append(element('strong', null, record.name));
      content.append(element('p', null, U.fullAddress(record)));
      const directions = U.directionsURL(record);
      if (directions) content.append(link('Get directions', directions, true));
      this.infoWindow.setContent(content);
      this.returnFocus = openMap && this.cards.get(id) ? this.cards.get(id).querySelector('.mm-dealer-card__map-button') : null;
      this.infoWindow.open({ map, anchor: entry.marker, shouldFocus: openMap && this.root.dataset.view === 'map' });
    }
    destroy() {
      ++this.sequence;
      this.abort.abort();
      this.markers.forEach(({ marker }) => { if (window.google) window.google.maps.event.clearInstanceListeners(marker); marker.setMap(null); });
      if (this.infoWindow) {
        if (window.google) window.google.maps.event.clearInstanceListeners(this.infoWindow);
        this.infoWindow.close();
      }
    }
  }
  function boot(scope) {
    const roots = [...(scope || document).querySelectorAll('[data-mm-dealer-locator]')];
    if (scope && scope.matches && scope.matches('[data-mm-dealer-locator]')) roots.unshift(scope);
    for (const root of roots) if (!instances.has(root)) instances.set(root, new DealerLocator(root));
  }
  window.MMDealerLocator = { boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) if (event.target.contains(root)) { instance.destroy(); instances.delete(root); }
  });
})();
